import type { ItemDto, ReceiptDto } from "@home/shared";
import { useState, type ReactNode } from "react";
import { ApiError, api } from "../api.ts";
import { t } from "../i18n.ts";
import { compressImage } from "../photo.ts";
import { useSync } from "../store.ts";
import { canScanQr, hapticTap, scanReceiptQr } from "../telegram.ts";
import { Sheet } from "./Sheet.tsx";

type Stage =
  | { name: "choose" }
  | { name: "busy"; text: string }
  | { name: "result"; receipt: ReceiptDto }
  | { name: "saved"; receipt: ReceiptDto };

const fmt = (n: number) => `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(n)}`;

function errorText(err: unknown): string {
  if (err instanceof ApiError && err.code.startsWith("err.")) {
    const known = ["err.receipt_qr_invalid", "err.receipt_not_found", "err.receipt_unavailable", "err.receipt_unreadable", "err.receipt_refund", "err.receipt_is_payment"] as const;
    const code = known.find((k) => k === err.code);
    if (code) return t(code);
  }
  if (err instanceof Error && err.name === "NetworkError") return t("needOnline");
  return t("err.generic");
}

/** Чек → сверка с текущим списком → сохранение расхода и отметки купленного. */
export function ReceiptSheet({ listId, items, onClose }: { listId: string; items: ItemDto[]; onClose: () => void }) {
  const { status } = useSync();
  const online = status === "online" || status === "syncing";
  const [stage, setStage] = useState<Stage>({ name: "choose" });
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState("");

  const run = async (load: () => Promise<ReceiptDto>) => {
    setError(null);
    try {
      setStage({ name: "busy", text: t("reading") });
      const receipt = await load();
      if (receipt.status === "saved") return setStage({ name: "saved", receipt });
      setStage({ name: "busy", text: t("matching") });
      setStage({ name: "result", receipt: await api.matchReceipt(receipt.id, listId) });
    } catch (err) {
      setError(errorText(err));
      setStage({ name: "choose" });
    }
  };

  const scan = async () => {
    const qr = await scanReceiptQr(t("scanPrompt")).catch(() => null);
    if (qr) void run(() => api.receiptFromQr(qr));
  };

  const save = async (receipt: ReceiptDto, markBought: boolean) => {
    hapticTap();
    setStage({ name: "busy", text: t("reading") });
    try {
      setStage({ name: "saved", receipt: await api.saveReceipt(receipt.id, markBought) });
    } catch (err) {
      setError(errorText(err));
      setStage({ name: "result", receipt });
    }
  };

  const nameOf = (id: string) => items.find((i) => i.id === id)?.name ?? "?";

  return (
    <Sheet title={t("checkReceipt")} onClose={onClose}>
      {error && <div className="banner banner-error receipt-error">{error}</div>}

      {stage.name === "choose" && (
        <div className="form">
          {!online && <div className="hint">{t("needOnline")}</div>}
          {canScanQr() && (
            <button className="btn btn-primary" disabled={!online} onClick={() => void scan()}>
              📷 {t("scanQr")}
            </button>
          )}
          <label className={online ? "btn" : "btn disabled"}>
            🧾 {t("receiptPhoto")}
            <input
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              disabled={!online}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void run(async () => api.receiptFromPhoto(await compressImage(file, 2000)));
              }}
            />
          </label>
          <form
            className="field-row"
            onSubmit={(e) => {
              e.preventDefault();
              if (link.trim()) void run(() => api.receiptFromQr(link.trim()));
            }}
          >
            <input className="input" placeholder={t("pasteLink")} value={link} onChange={(e) => setLink(e.target.value)} inputMode="url" />
            <button className="btn" type="submit" disabled={!online || !link.trim()}>
              {t("check")}
            </button>
          </form>
        </div>
      )}

      {stage.name === "busy" && <p className="empty">⏳ {stage.text}</p>}

      {(stage.name === "result" || stage.name === "saved") && (
        <div className="receipt">
          <div className="receipt-head">
            <div className="receipt-store">{stage.receipt.storeName ?? t("receipt")}</div>
            <div className="receipt-total">
              {t("total")}: <b>{fmt(stage.receipt.total)}</b>
            </div>
          </div>

          {stage.receipt.match && (
            <>
              <Section title={`✅ ${t("bought")}`} count={stage.receipt.match.bought.length}>
                {stage.receipt.match.bought.map((b) => (
                  <li key={b.listItemId}>
                    <b>{nameOf(b.listItemId)}</b>
                    <span className="receipt-src">← {b.receiptIndexes.map((i) => stage.receipt.items[i]?.name).join(", ")}</span>
                  </li>
                ))}
              </Section>
              <Section title={`➕ ${t("extra")}`} count={stage.receipt.match.extra.length}>
                {stage.receipt.match.extra.map((i) => {
                  const it = stage.receipt.items[i]!;
                  return (
                    <li key={i}>
                      {it.name}
                      <span className="receipt-amount">{fmt(it.amount)}</span>
                    </li>
                  );
                })}
              </Section>
              <Section title={`❗ ${t("missing")}`} count={stage.receipt.match.missing.length}>
                {stage.receipt.match.missing.map((id) => (
                  <li key={id}>{nameOf(id)}</li>
                ))}
              </Section>
            </>
          )}

          {stage.name === "result" ? (
            <div className="form receipt-actions">
              <button className="btn btn-primary" onClick={() => void save(stage.receipt, true)}>
                {t("saveMark")}
              </button>
              <button className="btn" onClick={() => void save(stage.receipt, false)}>
                {t("saveOnly")}
              </button>
            </div>
          ) : (
            <p className="receipt-saved">✅ {stage.receipt.transactionId ? t("receiptSaved") : t("alreadySaved")}</p>
          )}
        </div>
      )}
    </Sheet>
  );
}

function Section({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  if (count === 0) return null;
  return (
    <div className="receipt-section">
      <div className="section-title">
        {title} · {count}
      </div>
      <ul>{children}</ul>
    </div>
  );
}
